import { useLingui } from '@lingui/react/macro';
import { Check, ChevronDown, X } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { useRef, type ReactNode } from 'react';

import { cn } from './cn';
import { fieldControlClass } from './input';

export interface SelectOption {
  readonly value: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
}

export interface SelectProps {
  readonly options: readonly SelectOption[];
  /** Valor da opção escolhida. A opção é achada pelo valor, nunca pelo rótulo (discovery/05 §2.2). */
  readonly value: string | null | undefined;
  readonly onChange?: (value: string) => void;
  readonly placeholder?: string;
  readonly id?: string;
  readonly name?: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly required?: boolean;
  readonly className?: string;
  readonly emptyMessage?: ReactNode;
  /**
   * Botão "x" para limpar a escolha (`showButtonClearValue` do legado): aparece só com valor e
   * quando o campo é editável. Quem chama decide o vazio (`null`, `''`).
   */
  readonly onClear?: () => void;
  /** Nome acessível do "x"; padrão "Limpar seleção". Limpar devolve o foco ao gatilho. */
  readonly clearLabel?: string;
  readonly 'aria-labelledby'?: string;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: boolean;
  readonly 'aria-required'?: boolean;
  readonly 'aria-label'?: string;
}

/** Lista fechada de opções (Radix Select). Para busca ou lista remota, use `Combobox`. */
export function Select({
  options,
  value,
  onChange,
  placeholder,
  id,
  name,
  disabled,
  readOnly,
  required,
  className,
  emptyMessage,
  onClear,
  clearLabel,
  ...aria
}: SelectProps) {
  const { t } = useLingui();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const known = value !== null && value !== undefined && options.some((option) => option.value === value);
  const clearable = onClear !== undefined && known && disabled !== true && readOnly !== true;
  const trigger = (
    <SelectPrimitive.Trigger
      ref={triggerRef}
      id={id}
      data-slot="select-trigger"
      className={cn(
        fieldControlClass,
        'justify-between gap-2 text-left data-placeholder:text-muted-foreground [&>span]:truncate',
        onClear === undefined ? className : 'w-full',
        clearable && 'pr-11',
      )}
      {...aria}
    >
      <SelectPrimitive.Value placeholder={placeholder ?? t`Selecione`} />
      <SelectPrimitive.Icon asChild>
        <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
  return (
    <SelectPrimitive.Root
      value={known ? value : ''}
      onValueChange={(next) => onChange?.(next)}
      disabled={disabled === true || readOnly === true}
      {...(name === undefined ? {} : { name })}
      {...(required === undefined ? {} : { required })}
    >
      {onClear === undefined ? (
        trigger
      ) : (
        <div data-slot="select-clearable" className={cn('relative flex min-w-0 items-center', className)}>
          {trigger}
          {clearable ? (
            <button
              type="button"
              data-slot="select-clear"
              aria-label={clearLabel ?? t`Limpar seleção`}
              onClick={() => {
                onClear();
                // O "x" some com o valor; o foco volta ao gatilho em vez de cair no body.
                triggerRef.current?.focus();
              }}
              className="absolute right-8 inline-flex size-6 items-center justify-center rounded-control text-muted-foreground transition-colors duration-(--lume-dur-instant) hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
            >
              <X aria-hidden className="size-3.5" />
            </button>
          ) : null}
        </div>
      )}
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          data-slot="select-content"
          className={cn(
            'z-50 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-3',
            'origin-(--radix-select-content-transform-origin) data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in',
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.length === 0 ? (
              <p className="px-2 py-1.5 text-sm text-muted-foreground">{emptyMessage ?? t`Nenhuma opção disponível`}</p>
            ) : (
              options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled === true}
                  className={cn(
                    'relative flex min-h-10 items-center rounded-md py-1.5 pr-8 pl-2 text-control outline-none select-none md:min-h-8',
                    'transition-colors duration-100 data-highlighted:bg-accent-wash data-highlighted:text-accent-text data-highlighted:[&_svg]:text-accent-text data-[state=checked]:font-medium data-disabled:opacity-45',
                  )}
                >
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="absolute right-2 inline-flex items-center text-accent-text">
                    <Check aria-hidden className="size-4" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))
            )}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
